import Stack from "@mui/material/Stack";
import Badge from "@mui/material/Badge";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import Drawer, { drawerClasses } from "@mui/material/Drawer";
import BaseOptions from "./base-option";
import LayoutOptions from "./layout-options";
import StretchOptions from "./stretch-options";
import { useSettingsContext } from "../context";
import FullScreenOption from "./fullscreen-option";
import Iconify from "../../../iconify/iconify";
import Scrollbar from "../../../layout-components/scrollbar";
import { paper } from "../../../../theme/css";
import { useTranslate } from "src/locales";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";

// ----------------------------------------------------------------------

export default function SettingsDrawer() {
  const theme = useTheme();
    const { t } = useTranslate(); // Get the translate function from the hook

  const settings = useSettingsContext();

  const labelStyles = {
    mb: 1.5,
    color: "text.disabled",
    fontWeight: "fontWeightSemiBold",
  };

  const renderHead = (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ py: 2, pr: 1, pl: 2.5 }}
    >
      <ByzMuiTypography variant="h6" sx={{ flexGrow: 1 }}>
        {t("settings")}
      </ByzMuiTypography>

      <Tooltip title={t("reset")}>
        <IconButton onClick={settings.onReset}>
          <Badge color="error" variant="dot" invisible={!settings.canReset}>
            <Iconify icon="solar:restart-bold" />
          </Badge>
        </IconButton>
      </Tooltip>

      <IconButton onClick={settings.onClose}>
        <Iconify icon="mingcute:close-line" />
      </IconButton>
    </Stack>
  );

  const renderMode = (
    <div>
      <ByzMuiTypography variant="caption" sx={{ ...labelStyles }}>
        {t("mode")}
      </ByzMuiTypography>

      <BaseOptions
        value={settings.themeMode}
        onChange={(newValue: string) =>
          settings.onUpdate("themeMode", newValue)
        }
        options={["light", "dark"]}
        icons={["sun", "moon"]}
      />
    </div>
  );

  const renderContrast = (
    <div>
      <ByzMuiTypography variant="caption" sx={{ ...labelStyles }}>
        {t("contrast")}
      </ByzMuiTypography>

      <BaseOptions
        value={settings.themeContrast}
        onChange={(newValue: string) =>
          settings.onUpdate("themeContrast", newValue)
        }
        options={["default", "bold"]}
        icons={["contrast", "contrast_bold"]}
      />
    </div>
  );

  const renderDirection = (
    <div>
      <ByzMuiTypography variant="caption" sx={{ ...labelStyles }}>
        {t("direction")}
      </ByzMuiTypography>

      <BaseOptions
        value={settings.themeDirection}
        onChange={(newValue: string) =>
          settings.onUpdate("themeDirection", newValue)
        }
        options={["ltr", "rtl"]}
        icons={["align_left", "align_right"]}
      />
    </div>
  );

  const renderLayout = (
    <div>
      <ByzMuiTypography variant="caption" sx={{ ...labelStyles }}>
        {t("layout")}
      </ByzMuiTypography>

      <LayoutOptions
        value={settings.themeLayout}
        onChange={(newValue: string) =>
          settings.onUpdate("themeLayout", newValue)
        }
        options={["vertical", "horizontal", "mini"]}
      />
    </div>
  );

  const renderStretch = (
    <div>
      <ByzMuiTypography
        variant="caption"
        sx={{
          ...labelStyles,
          display: "inline-flex",
          alignItems: "center",
        }}
      >
        {t("stretch")}
        <Tooltip title={t("tooltip_stretch")}>
          <Iconify icon="eva:info-outline" width={16} sx={{ ml: 0.5 }} />
        </Tooltip>
      </ByzMuiTypography>

      <StretchOptions
        value={settings.themeStretch}
        onChange={() =>
          settings.onUpdate("themeStretch", !settings.themeStretch)
        }
      />
    </div>
  );

  return (
    <Drawer
      anchor="right"
      open={settings.open}
      onClose={settings.onClose}
      slotProps={{
        backdrop: { invisible: true },
      }}
      sx={{
        [`& .${drawerClasses.paper}`]: {
          ...paper({ theme, bgcolor: theme.palette.background.default }),
          width: 280,
        },
      }}
    >
      {renderHead}

      <Divider sx={{ borderStyle: "dashed" }} />

      <Scrollbar>
        <Stack spacing={3} sx={{ p: 3 }}>
          {renderMode}

          {renderContrast}

          {renderDirection}

          {renderLayout}

          {renderStretch}
        </Stack>
      </Scrollbar>

      <FullScreenOption />
    </Drawer>
  );
}
